import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import { Article } from '../../types/Article';
import { SearchWordListAttribute } from '../../types/SearchWordListAttribute';
import { SideMenuType } from '../../types/SideMenuType';
import { TagAttribute } from '../../types/TagAttribute';
import { ArticleCard } from '../Article/ArticleCard';
import { BlockSearchTermKeyword } from '../Block/BlockSearchTermKeyword';
import { GoogleAdManagerSlot } from '../GoogleAdManagerSlot';
import { SearchWordList } from '../SearchWordList';
import { Title } from '../Title';

import { BannerGroup } from './BannerGroup';
import { BannerProps } from './BannerProps';
import styles from './SideMenu.module.scss';
import {
  defaultUpperBannerList,
  defaultBottomBannerList,
} from './defaultParams';

import type { FC } from 'react';

import { assertNever, type AppEnv } from '@/packages/nurse-senka-frontend-sdk';

export type SideMenuProps = {
  sideMenuType: SideMenuType;
  upperBannerList?: BannerProps[];
  bottomBannerList?: BannerProps[];
  abcSearchAttribute?: SearchWordListAttribute[];
  knowledgeRankingArticles?: Article[];
  squareRankingArticles?: Article[];
  appEnv: AppEnv;
  relatedTags?: TagAttribute[];
};

export type SideMenuTermProps = {
  upperBannerList?: BannerProps[];
  bottomBannerList?: BannerProps[];
  abcSearchAttribute?: SearchWordListAttribute[];
  knowledgeRankingArticles?: Article[];
  squareRankingArticles?: Article[];
  appEnv: AppEnv;
  relatedTags?: TagAttribute[];
};

export type SideMenuDefaultProps = {
  upperBannerList?: BannerProps[];
  bottomBannerList?: BannerProps[];
  abcSearchAttribute?: SearchWordListAttribute[];
  knowledgeRankingArticles?: Article[];
  squareRankingArticles?: Article[];
  appEnv: AppEnv;
};

export const SideMenuTerm: FC<SideMenuTermProps> = ({
  upperBannerList = defaultUpperBannerList,
  bottomBannerList = defaultBottomBannerList,
  knowledgeRankingArticles,
  squareRankingArticles,
  abcSearchAttribute,
  appEnv,
  relatedTags,
}) => (
  <div className={styles.wrap}>
    <div className={styles.block}>
      <BlockSearchTermKeyword relatedTags={relatedTags} />
    </div>

    <div className={styles.block}>
      <BannerGroup bannerPropsList={upperBannerList} />
    </div>

    <div className={styles.block}>
      <GoogleAdManagerSlot
        key="div-gpt-ad-1426472053621-0"
        id="div-gpt-ad-1426472053621-0"
      />
      <GoogleAdManagerSlot
        key="div-gpt-ad-1426470100054-7"
        id="div-gpt-ad-1426470100054-7"
      />
    </div>

    {knowledgeRankingArticles && squareRankingArticles ? (
      <div className={styles.block}>
        <Tabs forceRenderTabPanel={true}>
          <TabList>
            <Tab>看護知識ランキング</Tab>
            <Tab>看護記事ランキング</Tab>
          </TabList>

          <TabPanel>
            <ArticleCard
              appEnv={appEnv}
              rank={true}
              type="side"
              articles={knowledgeRankingArticles}
            />
          </TabPanel>
          <TabPanel>
            <ArticleCard
              appEnv={appEnv}
              rank={true}
              type="side"
              articles={squareRankingArticles}
            />
          </TabPanel>
        </Tabs>
      </div>
    ) : (
      ''
    )}

    {abcSearchAttribute ? (
      <div className={styles.block}>
        <Title type="h2" title="50音順から検索" />
        <SearchWordList type="default" attributes={abcSearchAttribute} />
      </div>
    ) : (
      ''
    )}

    <div className={styles.block}>
      <BannerGroup bannerPropsList={bottomBannerList} />
    </div>

    <GoogleAdManagerSlot
      key="div-gpt-ad-1426470100054-9"
      id="div-gpt-ad-1426470100054-9"
    />
  </div>
);

export const SideMenuDefault: FC<SideMenuDefaultProps> = ({
  upperBannerList = defaultUpperBannerList,
  bottomBannerList = defaultBottomBannerList,
  knowledgeRankingArticles,
  squareRankingArticles,
  abcSearchAttribute,
  appEnv,
}) => (
  <div className={styles.wrap}>
    <div className={styles.block}>
      <BannerGroup bannerPropsList={upperBannerList} />
    </div>

    <div className={styles.block}>
      <GoogleAdManagerSlot
        key="div-gpt-ad-1426472053621-0"
        id="div-gpt-ad-1426472053621-0"
      />
      <GoogleAdManagerSlot
        key="div-gpt-ad-1426470100054-7"
        id="div-gpt-ad-1426470100054-7"
      />
    </div>

    {knowledgeRankingArticles && squareRankingArticles ? (
      <div className={styles.block}>
        <Tabs forceRenderTabPanel={true}>
          <TabList>
            <Tab>看護知識ランキング</Tab>
            <Tab>看護記事ランキング</Tab>
          </TabList>

          <TabPanel>
            <ArticleCard
              appEnv={appEnv}
              rank={true}
              type="side"
              articles={knowledgeRankingArticles}
            />
          </TabPanel>
          <TabPanel>
            <ArticleCard
              appEnv={appEnv}
              rank={true}
              type="side"
              articles={squareRankingArticles}
            />
          </TabPanel>
        </Tabs>
      </div>
    ) : (
      ''
    )}

    {abcSearchAttribute ? (
      <div className={styles.block}>
        <Title type="h2" title="50音順から検索" />
        <SearchWordList type="default" attributes={abcSearchAttribute} />
      </div>
    ) : (
      ''
    )}

    <div className={styles.block}>
      <BannerGroup bannerPropsList={bottomBannerList} />
    </div>

    <GoogleAdManagerSlot
      key="div-gpt-ad-1426470100054-9"
      id="div-gpt-ad-1426470100054-9"
    />
  </div>
);

export const SideMenu: FC<SideMenuProps> = ({
  sideMenuType,
  knowledgeRankingArticles,
  squareRankingArticles,
  abcSearchAttribute,
  appEnv,
  relatedTags,
}) => {
  switch (sideMenuType) {
    case 'term':
      return (
        <SideMenuTerm
          appEnv={appEnv}
          knowledgeRankingArticles={knowledgeRankingArticles}
          squareRankingArticles={squareRankingArticles}
          relatedTags={relatedTags}
          abcSearchAttribute={abcSearchAttribute}
        />
      );
    case 'default':
      return (
        <SideMenuDefault
          appEnv={appEnv}
          knowledgeRankingArticles={knowledgeRankingArticles}
          squareRankingArticles={squareRankingArticles}
          abcSearchAttribute={abcSearchAttribute}
        />
      );
    default:
      return assertNever(sideMenuType);
  }
};
